import type { UppyFile } from "@uppy/core";
import cuid from "cuid";
import { Base64 } from "js-base64";
import { useCallback, useContext, useState } from "react";
import * as tus from "tus-js-client";

import { client } from "@/config";
import { createLogger } from "@/logger";
import { AppContext } from "@/models/AppStateProvider";
import { DataContext } from "@/models/DataProvider";
import { CurrentFeedContext } from "@/models/StateProviders/currentFeedProvider";
import { MyAccountContext } from "@/models/StateProviders/myAccountProvider";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider";
import { TrackingContext } from "@/models/TrackingStateProvider";
import { buildFileMetaData, fetchUploadConfig } from "@/utils";

const log = createLogger("upload");

function toUppyFileShape(file: File): UppyFile<Record<string, unknown>, Record<string, unknown>> {
  const ext = file.name.includes(".") ? (file.name.split(".").pop() ?? "") : "";
  return {
    id: "scheduled-local",
    name: file.name,
    extension: ext,
    type: file.type,
    data: file,
    meta: { name: file.name },
    progress: { uploadComplete: false, bytesUploaded: 0, bytesTotal: file.size, uploadStarted: Date.now() },
    size: file.size,
    isRemote: false,
    isGhost: false,
  };
}

function toTusStringMetadata(meta: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(meta)) {
    if (v === undefined || v === null) continue;
    out[k] = typeof v === "string" ? v : String(v);
  }
  return out;
}

/** TUS upload for scheduled-message file workflow — omits feedId in metadata so nothing publishes on upload. */
export function useScheduledMessageTusUpload() {
  const { config } = useContext(AppContext);
  const { preferredLanguage } = useContext(DataContext);
  const { ampli } = useContext(TrackingContext);
  const { currentFeedId: feedId } = useContext(CurrentFeedContext);
  const { myAccountId } = useContext(MyAccountContext);
  const { currentWorkspaceId: workspaceId } = useContext(WorkspaceContext);
  const { stage, tusUrl } = fetchUploadConfig(config);
  const [bytesUploaded, setBytesUploaded] = useState(0);
  const [bytesTotal, setBytesTotal] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const reset = useCallback(() => {
    setError(null);
    setBytesUploaded(0);
    setBytesTotal(0);
  }, []);

  const uploadFile = useCallback(
    async (file: File, displayName: string): Promise<{ contentId: string }> => {
      if (!tusUrl) {
        throw new Error("Upload is not configured (tusUrl missing).");
      }

      setError(null);
      setIsUploading(true);

      const contentId = cuid();
      const itemId = cuid();
      const startTime = new Date();
      const uppyFile = toUppyFileShape(file);

      await client.createContentEvent({
        contentId,
        workspaceId,
        step: "client_processing",
        status: "started",
        context: { type: "file" },
        feedId,
      });

      const fileMeta = await buildFileMetaData({
        file: uppyFile,
        stage,
        feedId: "",
        itemId,
        myAccount: myAccountId,
        uniqueUploadId: contentId,
      });
      const { feedId: _dropped, ...metaWithoutFeed } = fileMeta as { feedId?: string } & Record<string, unknown>;

      const merged: Record<string, unknown> = {
        ...metaWithoutFeed,
        contentId,
        startTime: startTime.toISOString(),
        preferredLanguage: preferredLanguage ?? "",
        inputType: "File",
        isSilent: "true",
      };
      if (displayName) {
        merged.title = Base64.encode(displayName);
        merged.description = Base64.encode("");
      }

      await client.createContentEvent({
        contentId,
        workspaceId,
        step: "client_uploading",
        status: "started",
        context: { type: "file" },
        feedId,
      });
      ampli.fileUploadPhotoGallery();
      ampli.fileUploadSubmit();

      const stringMeta = toTusStringMetadata(merged);

      return new Promise((resolve, reject) => {
        const upload = new tus.Upload(file, {
          endpoint: tusUrl,
          retryDelays: [0, 1000, 3000, 5000],
          removeFingerprintOnSuccess: true,
          storeFingerprintForResuming: true,
          metadata: stringMeta,
          onError: (err) => {
            setIsUploading(false);
            log.error("Scheduled TUS upload failed", err);
            setError(err instanceof Error ? err : new Error(String(err)));
            ampli.fileUploadError();
            void client.createContentEvent({
              contentId,
              workspaceId,
              step: "client_uploading",
              status: "failed",
              context: { type: "file" },
              feedId,
              error: err instanceof Error ? err : new Error(String(err)),
            });
            reject(err);
          },
          onProgress: (uploaded, total) => {
            setBytesUploaded(uploaded);
            setBytesTotal(total);
          },
          onSuccess: () => {
            setIsUploading(false);
            const duration = (new Date().getTime() - startTime.getTime()) / 1000;
            void client.createContentEvent({
              contentId,
              workspaceId,
              step: "client_uploading",
              status: "finished",
              context: { type: "file", duration },
              feedId,
            });
            setBytesTotal(file.size);
            setBytesUploaded(file.size);
            resolve({ contentId });
          },
        });
        upload.start();
      });
    },
    [ampli, feedId, myAccountId, preferredLanguage, stage, tusUrl, workspaceId],
  );

  const progressFraction = bytesTotal > 0 ? bytesUploaded / bytesTotal : 0;

  return { uploadFile, isUploading, error, progressFraction, bytesUploaded, bytesTotal, reset };
}
